import {
  DEFAULT_TRAIN_AI_TIME_WINDOW,
  HotTopicConversationFilter,
  TRAIN_AI_TIME_WINDOWS,
} from '@conversed/shared/src/central/constants/hot-topics';
import { z } from 'zod';

export const HotTopicConversationSummarySchema = z.object({
  _id: z.string(),
  question: z.string(),
  answer: z.string(),
  isIntentRedirect: z.boolean().optional(),
});

export const HotTopicConversationSummariesResponseSchema = z.object({
  results: z.array(HotTopicConversationSummarySchema),
  totalRecords: z.number(),
});

export const HotTopicOverviewItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDeleted: z.boolean(),
  volume: z.number(),
  preApprovedCount: z.number(),
  toReviewCount: z.number(),
  lastTrainedAt: z.string().nullable(),
});

export const HotTopicsOverviewResponseSchema = z.object({
  topics: z.array(HotTopicOverviewItemSchema),
  // Null when the pre-approval rate is not yet available (computed by a separate task).
  reviewAgentStatus: z
    .object({ preApprovedPercentage: z.number() })
    .nullable()
    .optional(),
  // Admin-configured bot name (platformSettings.botName), with Core LLM-block name as fallback
  botName: z.string(),
});

// * The Train A.I. page's time window. Every studio defaults to the same one, and
// * All time is refused: Review loads a topic's questions unpaginated.
const TrainAITimeWindowSchema = z
  .enum(TRAIN_AI_TIME_WINDOWS)
  .default(DEFAULT_TRAIN_AI_TIME_WINDOW);

export const HotTopicsOverviewQuerySchema = z.object({
  timeWindow: TrainAITimeWindowSchema,
});

export const HotTopicConversationsQuerySchema = z.object({
  hotTopicId: z.string(),
  timeWindow: TrainAITimeWindowSchema,
  filter: z
    .enum(HotTopicConversationFilter)
    .optional()
    .default(HotTopicConversationFilter.ALL),
});

export const SetConversationHotTopicBodySchema = z.object({
  hotTopicId: z.string(),
});

export const SetConversationHotTopicParamsSchema = z.object({
  conversationId: z.string(),
});

export const ConversationThreadParamsSchema = z.object({
  conversationId: z.string(),
});

export const ConversationThreadMessageSchema = z.object({
  _id: z.string(),
  question: z.string(),
  answer: z.string(),
  isIntentRedirect: z.boolean().optional(),
  createdAt: z.string(),
  hotTopic: z.object({ id: z.string(), name: z.string() }).nullable(),
});

export const ConversationThreadResponseSchema = z.object({
  results: z.array(ConversationThreadMessageSchema),
});

// * The page sizes every Train A.I. overview table offers; the Voice call list
// * endpoint accepts only these.
export const TRAIN_AI_PER_PAGE_OPTIONS = [10, 25] as const;

export const VoiceConversationsQuerySchema = z.object({
  timeWindow: TrainAITimeWindowSchema,
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce
    .number()
    .int()
    .refine((perPage) =>
      TRAIN_AI_PER_PAGE_OPTIONS.some((option) => option === perPage),
    )
    .default(TRAIN_AI_PER_PAGE_OPTIONS[0]),
});

// * The content fields are null when the call's detail could not be read, or
// * (opening question) when the caller never spoke.
export const VoiceConversationListItemSchema = z.object({
  conversationId: z.string(),
  occurredAt: z.string(),
  callDurationSecs: z.number(),
  openingQuestion: z.string().nullable(),
  exchangeCount: z.number().nullable(),
  summary: z.string().nullable(),
});

export const VoiceConversationsResponseSchema = z.object({
  items: z.array(VoiceConversationListItemSchema),
  total: z.number(),
});

export const VoiceConversationParamsSchema = z.object({
  conversationId: z.string(),
});

export const VoiceConversationExchangeSchema = z.object({
  question: z.string(),
  answer: z.string(),
});

export const VoiceConversationResponseSchema = z.object({
  conversationId: z.string(),
  occurredAt: z.string(),
  exchanges: z.array(VoiceConversationExchangeSchema),
});
